import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { useProductsInfos } from '../../common/hooks/useProductsInfos';
import { RecommendationsHeader } from '../../recommendations/components/RecommendationsHeader';
import { RecommendationsContent } from '../../recommendations/components/RecommendationsContent';
import { breakpoints, colors, durations, opacities } from '../../../design-system';
import { Product } from '../../product/types';
import { loadRecommendationsHomemade } from '../../recommendations/utils';

const StyledRecommendationsTabsBannerContainer = styled.div`
  margin: 0 16px;

  @media (min-width: ${breakpoints.S}px) {
    margin: 0;
  }

  .recommendations-tabs-buttons-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    justify-content: center;
    padding-bottom: 24px;
    width: 100%;

    @media (min-width: ${breakpoints.S}px) {
      width: fit-content;
      margin: auto;
    }
  }

  .recommendations-tabs-button {
    border: none;
    border-bottom: 1px solid ${colors.LIGHT};
    color: ${colors.BLACK};
    padding: 0;
    background-color: unset;
    cursor: pointer;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;

    @media (min-width: ${breakpoints.S}px) {
      padding: 0 24px;
    }

    @media (hover: hover) {
      &:hover {
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .button-selected {
    border-bottom: 1px solid ${colors.BLACK};
    pointer-events: none;
  }

  .button-name {
    margin: 0;
    padding: 16px 0;
    font-size: 1.4rem;
    color: ${colors.GREY2};
    text-transform: capitalize;

    @media (min-width: ${breakpoints.S}px) {
      font-size: 1.6rem;
    }
  }

  .button-name-selected {
    font-weight: 700;
    color: ${colors.BLACK};
  }
`;

type Props = {
  title: string;
  tabs?: { name?: string; tab_title?: string }[];
};

export const RecommendationsTabsBanner = ({ title, tabs }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [recommendedRefs, setRecommendedRefs] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  const [selectedFilter, setSelectedFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    const currentRef = containerRef?.current;
    if (currentRef) {
      currentRef.scrollTo({ left: 0, behavior: 'instant' });
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (tabs?.length) {
      setSelectedFilter(tabs[0].name?.toLocaleLowerCase());
    }
  }, [recommendedRefs]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const data = await loadRecommendationsHomemade({
          type: 'home',
          params: {},
        });

        if (data) {
          setRecommendedRefs(data);
        } else {
          setRecommendedRefs({});
        }
      } catch (e) {
        setRecommendedRefs({});
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  const selectedCategoriesSet = useMemo(() => new Set(tabs?.map((tab) => tab.name)), [tabs]);

  const filteredProducts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(recommendedRefs).filter(([category]) => selectedCategoriesSet.has(category))
      ),
    [recommendedRefs, selectedCategoriesSet]
  );

  const allProductRefs = useMemo(() => Object.values(filteredProducts).flat(), [filteredProducts]);

  const { hits: unorderedHits } = useProductsInfos(allProductRefs);

  const reclassifiedProducts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filteredProducts).map(([category, refs]) => [
        category,
        (Array.isArray(refs) ? refs : [])
          .map((ref: string) =>
            unorderedHits.find((product) => `${product.productRef}-${product.colorRef}` === ref)
          )
          .filter(Boolean),
      ])
    );
  }, [filteredProducts, unorderedHits]);

  const categories = useMemo(() => Object.keys(reclassifiedProducts), [reclassifiedProducts]);

  const hits = useMemo(() => {
    return categories.reduce(
      (acc, category) => {
        acc[category] = (reclassifiedProducts[category] || []).filter(Boolean) as Product[];
        return acc;
      },
      {} as Record<string, Product[]>
    );
  }, [categories, reclassifiedProducts]);

  const hasData = Object.values(hits).some((arr) => Array.isArray(arr) && arr.length > 0);

  if (!hasData || !selectedFilter || loading) {
    return null;
  }

  return (
    <StyledRecommendationsTabsBannerContainer>
      <RecommendationsHeader
        id="home-page-tabs"
        title={title}
        hits={hits[selectedFilter]}
        containerRef={containerRef}
      />
      <div className="recommendations-tabs-buttons-wrapper">
        {tabs?.map((tab) => {
          return (
            <button
              key={tab.name}
              type="button"
              onClick={() => setSelectedFilter(tab.name?.toLocaleLowerCase())}
              className={`recommendations-tabs-button ${
                selectedFilter === tab.name?.toLocaleLowerCase() ? 'button-selected' : ''
              }`}
              data-testid={`hp-tabs-${tab.name}-recommendations-button`}
              aria-label={`Bouton pour afficher les produits ${tab.name}`}
            >
              <p
                className={`button-name ${selectedFilter === tab.name?.toLocaleLowerCase() ? 'button-name-selected' : ''}`}
              >
                {tab.tab_title || tab.name}
              </p>
            </button>
          );
        })}
      </div>
      <RecommendationsContent
        id="home-page-tabs"
        hits={hits[selectedFilter]}
        containerRef={containerRef}
      />
    </StyledRecommendationsTabsBannerContainer>
  );
};
